import { DatabaseObjectType } from '../models/databaseObjectTypes';
import { MessageTreeNode } from '../../explorer/models/DatabaseTreeModels';
import { sanitizeConnectionErrorMessage } from './connectionErrorUtils';

const objectFolderTypeMap: Record<string, DatabaseObjectType> = {
  'tables-folder': 'table',
  'views-folder': 'view',
  'stored-procedures-folder': 'stored-procedure',
  'functions-folder': 'function',
  'packages-folder': 'package',
  'sequences-folder': 'sequence',
  'synonyms-folder': 'synonym',
  'db-links-folder': 'db-link',
  'materialized-views-folder': 'materialized-view',
  'types-folder': 'type',
  // SQL Server: the `Types` node is a CONTAINER (`user-types-folder`) with no
  // listing of its own — only these three leaves fetch. That is why it is
  // absent from this map: `mapFolderTypeToObjectType` returning undefined is
  // what keeps the expand/refresh paths from replacing its subfolders with an
  // empty-listing node, exactly as it does for `programmability-folder`.
  'table-types-folder': 'table-type',
  'alias-types-folder': 'alias-type',
  'clr-types-folder': 'clr-type',
  'directories-folder': 'directory',
  'external-tables-folder': 'external-table',
  'property-graphs-folder': 'property-graph',
};

/**
 * Table subfolder types that can be expanded
 */
export type TableSubfolderType =
  | 'columns-folder'
  | 'keys-folder'
  | 'constraints-folder'
  | 'triggers-folder'
  | 'indexes-folder'
  | 'statistics-folder'
  | 'partitions-folder';

/**
 * Maps table subfolder types to their content type
 */
const tableSubfolderContentTypeMap: Record<TableSubfolderType, string> = {
  'columns-folder': 'column',
  'keys-folder': 'key',
  'constraints-folder': 'constraint',
  'triggers-folder': 'trigger',
  'indexes-folder': 'index',
  'statistics-folder': 'statistic',
  'partitions-folder': 'partition',
};

/**
 * Checks if a node type is a table subfolder
 */
export function isTableSubfolderType(nodeType: string): nodeType is TableSubfolderType {
  return nodeType in tableSubfolderContentTypeMap;
}

/**
 * Gets the content type for a table subfolder
 */
export function getTableSubfolderContentType(folderType: TableSubfolderType): string {
  return tableSubfolderContentTypeMap[folderType];
}

/**
 * Gets the label for a table subfolder type
 */
export function getTableSubfolderLabel(folderType: TableSubfolderType): string {
  switch (folderType) {
    case 'columns-folder':
      return 'columns';
    case 'keys-folder':
      return 'keys';
    case 'constraints-folder':
      return 'constraints';
    case 'triggers-folder':
      return 'triggers';
    case 'indexes-folder':
      return 'indexes';
    case 'statistics-folder':
      return 'statistics';
    case 'partitions-folder':
      return 'partitions';
    default:
      return 'items';
  }
}

export function mapFolderTypeToObjectType(nodeType: string): DatabaseObjectType | undefined {
  return objectFolderTypeMap[nodeType as keyof typeof objectFolderTypeMap];
}

export function getObjectLabel(objectType: DatabaseObjectType): string {
  switch (objectType) {
    case 'table':
      return 'tables';
    case 'view':
      return 'views';
    case 'stored-procedure':
      return 'stored procedures';
    case 'function':
      return 'functions';
    case 'package':
      return 'packages';
    case 'sequence':
      return 'sequences';
    case 'synonym':
      return 'synonyms';
    case 'db-link':
      return 'DB links';
    case 'materialized-view':
      return 'materialized views';
    case 'type':
      return 'types';
    case 'table-type':
      return 'user-defined table types';
    case 'alias-type':
      return 'user-defined data types';
    case 'clr-type':
      return 'user-defined types (CLR)';
    case 'directory':
      return 'directories';
    case 'external-table':
      return 'external tables';
    case 'property-graph':
      return 'property graphs';
    default:
      return 'objects';
  }
}

export function createEmptyMessageNode(
  connectionId: string,
  database: string,
  objectType: DatabaseObjectType,
  parentId: string,
): MessageTreeNode {
  return {
    id: `empty-${connectionId}-${database}-${objectType}`,
    parentId,
    name: `No ${getObjectLabel(objectType)} found`,
    type: 'message',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    icon: 'message',
    metadata: {
      connectionId,
      database,
    },
  };
}

export function createErrorNode(
  connectionId: string,
  database: string,
  objectType: DatabaseObjectType,
  parentId: string,
  errorMessage?: string,
): MessageTreeNode {
  const fallback = `Failed to load ${getObjectLabel(objectType)}`;
  const rawMessage = errorMessage || fallback;
  const message = sanitizeConnectionErrorMessage(rawMessage);

  return {
    id: `error-${connectionId}-${database}-${objectType}`,
    parentId,
    name: `Error: ${message}`,
    type: 'error',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      error: message,
    },
  };
}

/**
 * Creates an empty message node for table subfolders
 */
export function createTableSubfolderEmptyNode(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  folderType: TableSubfolderType,
  parentId: string,
): MessageTreeNode {
  const label = getTableSubfolderLabel(folderType);
  return {
    id: `empty-${connectionId}-${database}-${schema}-${tableName}-${folderType}`,
    parentId,
    name: `No ${label} found`,
    type: 'message',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    icon: 'message',
    metadata: {
      connectionId,
      database,
    },
  };
}

/**
 * Creates an error node for table subfolders
 */
export function createTableSubfolderErrorNode(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  folderType: TableSubfolderType,
  parentId: string,
  errorMessage?: string,
): MessageTreeNode {
  const label = getTableSubfolderLabel(folderType);
  const fallback = `Failed to load ${label}`;
  const rawMessage = errorMessage || fallback;
  const message = sanitizeConnectionErrorMessage(rawMessage);

  return {
    id: `error-${connectionId}-${database}-${schema}-${tableName}-${folderType}`,
    parentId,
    name: `Error: ${message}`,
    type: 'error',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      error: message,
    },
  };
}
